// import { getRoleMasterDetailApi } from 'api/roleMasterApi';
import { getStaffDetailApi } from 'api/authApi';
import { IRoleResponses } from 'types/roleMasterType';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getLocalStorage, removeLocalStorage, removeSessionStorage, setLocalStorage, setSessionStorage,
} from 'utility/browserStorageUtil';
import { JwtPayloadType } from 'types/authType';
// import axios from 'axios';
import {
  CODE_ADMIN, CODE_EDIT, CODE_EXCEL, CODE_IMPORT, CODE_ISDISABLE, CODE_MAILSEMD, CODE_MANAGERITEM, CODE_OUTPORT, FOLLOW_ORG_ID, /* LIST_FEATURE_CODE_FULL_LEVEL,*/
} from 'constants/role';
import { getAppliedDocsCountApi } from 'api/approvalManagementApi/requiredApplicationListApi';
import { getReservationInquiryCountApi } from 'api/inquiryApi';
import { saveApprovalDocumentAmountSideBar } from 'redux/approvalAmountDoc/actions';
import { saveReservationInquiryAmountSideBar } from 'redux/reservationInquiryAmount/actions';
import {
  setIsAuth, setStaffDetail,
} from '../redux/auth/actions';

/**
 * Check token's validity
 * if valid, set isAuth to True
 */
const useValidateToken = () => {
  const dispatch = useDispatch();
  const [isTokenChecked, setIsTokenChecked] = useState(false);
  const accessToken = getLocalStorage('accessToken');

  const decodeJwt = (token: string): JwtPayloadType => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`).join(''));

    return JSON.parse(jsonPayload);
  };

  /**
   * companyCode from URL
   * @returns string
   */
  const urlCompanyCode = () => {
    const path = window.location.pathname;
    return path.split('/')[1];
  };

  /**
   * Make sure user doesn't change companyCode in URL after logged in
   * @returns boolean
   */
  const checkCompanyCode = () => {
    const urlCompCode = urlCompanyCode();
    const { companyCode } = decodeJwt(accessToken);

    return urlCompCode !== companyCode;
  };

  const fetchSideBarCount = () => {
    getAppliedDocsCountApi().then((res) => {
      const {
        needToRegister = 0, needToApprove = 0, appliedDocsOfApproveResult = 0, appliedDocsOfApprovalApplicationReference = 0,
      } = res;
      const countApproverData = {
        requiredAmount: needToRegister,
        admitAmount: needToApprove,
        resultAmount: appliedDocsOfApproveResult,
        applicationApprovalReferenceAmount: appliedDocsOfApprovalApplicationReference,
      };
      dispatch(saveApprovalDocumentAmountSideBar(countApproverData));
    }).catch((error) => {
      console.log(error);
    });

    getReservationInquiryCountApi().then((res) => {
      const { unregisteredCount = 0 } = res || {};
      dispatch(saveReservationInquiryAmountSideBar({ unregisteredAmount: unregisteredCount }));
    }).catch((error) => {
      console.log(error);
    });
  };

  /**
   * Fetch API to validate accessToken from localStorage
   */
  const validate = async (tokenToValidate: string) => {
    if (isTokenChecked) return;
    try {
      if (!tokenToValidate) throw new Error();
      if (checkCompanyCode()) throw new Error();

      const { companyCode } = decodeJwt(tokenToValidate);
      const staffId = getLocalStorage('staffId');

      // set storage
      setLocalStorage('accessToken', tokenToValidate);
      setSessionStorage('companyCode', companyCode);

      const [staffDetail] = await Promise.all([getStaffDetailApi(companyCode, staffId)]);
      // const arrRole = staffDetail?.administratorFlag ? [...LIST_FEATURE_CODE_FULL_LEVEL] : staffDetail?.roles; // when staff is have checked full role
      const arrRole = staffDetail?.roles || [];
      const IS_ADMIN = staffDetail?.administratorFlag ?? false;
      // Type 1
      const roleDetailMap = new Map<IRoleResponses['featureCode'], IRoleResponses>(arrRole.map((item) => [item.featureCode, {
        ...item,
        accessLevel: { code: IS_ADMIN ? CODE_ADMIN : (Number(item?.accessLevel?.code ?? item?.accessLevel)), value: IS_ADMIN ? 'Admin' : String(item?.accessLevel?.value ?? item?.accessLevel) },
        followOrgId: IS_ADMIN ? FOLLOW_ORG_ID : (item?.followOrgId || ''),
        followOrganizationName: IS_ADMIN ? [] : (item?.followOrganizationName ?? []),
        edit: IS_ADMIN ? CODE_EDIT : item.edit,
        possibleToImport: IS_ADMIN ? CODE_IMPORT : item.possibleToImport,
        possibleToOutput: IS_ADMIN ? CODE_OUTPORT : item.possibleToOutput,
        possibleToManagerItem: IS_ADMIN ? CODE_MANAGERITEM : item.possibleToManagerItem,
        possibleToOutputExcel: IS_ADMIN ? CODE_EXCEL : item.possibleToOutputExcel,
        isDisabled: IS_ADMIN ? CODE_ISDISABLE : item.isDisabled,
        mailSend: IS_ADMIN ? CODE_MAILSEMD : item.mailSend,
      }]));
      // Type  2
      // const roleDetailMap = new Map<IRoleResponses['featureCode'], IRoleResponses>();
      // staffDetail.roles.forEach((roledetail) => {
      //   roleDetailMap.set(roledetail.featureCode, { ...roledetail });
      // });
      const orgAccess = roleDetailMap.get('master.organize');
      const exeAccess = roleDetailMap.get('master.executive');
      const staffAccess = roleDetailMap.get('master.staff');
      const roleAccess = roleDetailMap.get('master.security');
      const employAccess = roleDetailMap.get('master.employmentStatus');
      const customerAccess = roleDetailMap.get('customer.info');
      const customerMedicalRecord = roleDetailMap.get('customer.info.medicalRecord');
      const customerContractHistory = roleDetailMap.get('customer.info.contractHistory');
      const customerAccountHistory = roleDetailMap.get('customer.info.accountHistory');
      const customerProductHistory = roleDetailMap.get('customer.info.productHistory');
      const customerTicketHistory = roleDetailMap.get('customer.info.ticketHistory');
      const customerReservationHistory = roleDetailMap.get('customer.info.reservationHistory');
      const customerResponseInfo = roleDetailMap.get('customer.info.responseInfo');
      const customerManagerSetting = roleDetailMap.get('customer.info.managerSetting');
      const customerPointManagement = roleDetailMap.get('customer.info.point');
      const customerCommunication = roleDetailMap.get('customer.info.communication');
      const customerAttachedSheet = roleDetailMap.get('customer.info.karteSheet');
      const customerMeasuringSheet = roleDetailMap.get('customer.info.karteMeasuringInfo');
      const customerPhotoComparison = roleDetailMap.get('customer.info.kartePhoto');
      const customerDelete = roleDetailMap.get('customer.info.delete');
      const customerVisitInfo = roleDetailMap.get('customer.info.visit');
      const customerSalonHistory = roleDetailMap.get('customer.info.salonHistory');

      const reservationManagement = roleDetailMap.get('reservation.info');
      const reservationStoreSetting = roleDetailMap.get('reservation.info.storeSetting');
      const reservationManagementSetting = roleDetailMap.get('reservation.info.managerSetting');
      const reservationInquiry = roleDetailMap.get('reservation.info.inquiry');
      const reservationAreaMaster = roleDetailMap.get('reservation.info.areaMaster');
      const reservationNewCourseMaster = roleDetailMap.get('reservation.info.newCourseMaster');
      const reservationExistingCourseMaster = roleDetailMap.get('reservation.info.existingCourseMaster');
      const reservationAssignSetting = roleDetailMap.get('reservation.info.assignSetting');
      const reservationMailSetting = roleDetailMap.get('reservation.info.mailSetting');
      const reservationShiftSchedule = roleDetailMap.get('reservation.info.shiftSchedule');
      const reservationShiftManagement = roleDetailMap.get('reservation.info.shiftManagement');
      const reservationAgentMaster = roleDetailMap.get('reservation.info.agentMaster');
      const reservationCreativeMaster = roleDetailMap.get('reservation.info.creativeMaster');
      const reservationMediaPlacement = roleDetailMap.get('reservation.info.mediaPlacement');

      const commonSalesMenuSection = roleDetailMap.get('commonMaster.sales.menuSection');
      const commonSalesMenu = roleDetailMap.get('commonMaster.sales.menu');
      const commonContractMenuClass = roleDetailMap.get('commonMaster.contract.menuClass');
      const commonContractMenu = roleDetailMap.get('commonMaster.contract.menu');
      const commonContractCourseClass = roleDetailMap.get('commonMaster.contract.courseClass');
      const commonContractCourseCategory = roleDetailMap.get('commonMaster.contract.courseCategory');
      const commonContractCourse = roleDetailMap.get('commonMaster.contract.course');
      const commonRegisterMedium = roleDetailMap.get('commonMaster.register.medium');
      const commonRegisterMediumSubClass = roleDetailMap.get('commonMaster.register.mediumSubClass');
      const commonRegisterRate = roleDetailMap.get('commonMaster.register.rate');
      const commonRegisterDiscount = roleDetailMap.get('commonMaster.register.discount');
      const commonStockPurchase = roleDetailMap.get('commonMaster.stock.purchase');
      const commonPointSetting = roleDetailMap.get('commonMaster.point.setting');

      const registerManagement = roleDetailMap.get('register.info');
      const registerHeadquartersSetting = roleDetailMap.get('register.headquarters.setting');
      const registerStoreSetting = roleDetailMap.get('register.store.setting');

      const contractStatus = roleDetailMap.get('contract.contractStatus');
      const contractCancel = roleDetailMap.get('contract.cancel');
      const contractCorrection = roleDetailMap.get('contract.correct');
      const contractFlatRate = roleDetailMap.get('contract.contractFlatRate');
      const contractAutoCreateHistory = roleDetailMap.get('contract.contractAutoCreateHistory');
      const contractAutoTicketUseHistory = roleDetailMap.get('contract.contractAutoTicketUseHistory');

      const revenueShopBudgetSetting = roleDetailMap.get('sales.management.storeSetting.shopBudgetSetting');
      const revenueSalesDailyReport = roleDetailMap.get('sales.management.list.ciz.salesDailyReport');
      const revenueSalesMonthlyReport = roleDetailMap.get('sales.management.list.ciz.salesMonthlyReport');
      const revenueDailyTotal = roleDetailMap.get('sales.management.list.ciz.dailyTotal');
      const revenueSalesMonthlyReportList = roleDetailMap.get('sales.management.list.ciz.salesMonthlyReportList');
      const revenuePersonal = roleDetailMap.get('sales.management.list.ciz.personal');
      const revenueSalesMedia = roleDetailMap.get('sales.management.list.ciz.salesMedia');
      const revenueHeadquarterBudgetSetting = roleDetailMap.get('sales.management.headquartersSetting.shopBudgetSetting');
      const revenueAdReport = roleDetailMap.get('sales.management.list.ciz.adReport');
      const revenueSalesStoreMedia = roleDetailMap.get('sales.management.list.ciz.salesStoreMedia');
      const revenueSalesCourse = roleDetailMap.get('sales.management.list.ciz.salesCourse');
      const revenueShopItem = roleDetailMap.get('sales.management.list.ciz.shopItem');
      const revenueExpensiveUsedCustomer = roleDetailMap.get('sales.management.list.ciz.expensiveUsedCustomer');
      const revenueIncentive = roleDetailMap.get('sales.management.list.ciz.incentive');
      const revenueContinuousRate = roleDetailMap.get('sales.management.list.ciz.continuousRate');
      const revenueReservationAdvertisingMedia = roleDetailMap.get('sales.management.list.ciz.reservationAdvertisingMedia');
      const revenueAccountReceivable = roleDetailMap.get('sales.management.list.ciz.accountReceivable');
      const revenuePointUse = roleDetailMap.get('sales.management.list.ciz.pointUse');
      const revenuePointRemain = roleDetailMap.get('sales.management.list.ciz.pointRemain');
      const revenuePointUseRateReport = roleDetailMap.get('sales.management.list.ciz.pointUseRateReport');
      const revenueDailyTotalList = roleDetailMap.get('sales.management.list.ciz.dailyTotalList');
      const revenueSimpleSalesSimulation = roleDetailMap.get('sales.management.list.ciz.simpleSalesSimulationReport');
      const inventoryControl = roleDetailMap.get('stock.management');

      const mailManagement = roleDetailMap.get('mailMagazine.info');

      const approvalManagement = roleDetailMap.get('work_flow');
      const workflowHeadquartersSetting = roleDetailMap.get('workflow.managerSetting');

      if (IS_ADMIN) {
        staffDetail.masterRoleLevel = 0;
        staffDetail.customerRoleLevel = 0;
        staffDetail.reservationRoleLevel = 0;
        staffDetail.commonRoleLevel = 0;
        staffDetail.registerRoleLevel = 0;
        staffDetail.contractRoleLevel = 0;
        staffDetail.revenueRoleLevel = 0;
        staffDetail.inventoryRoleLevel = 0;
        staffDetail.magazineRoleLevel = 0;
        staffDetail.approvalRoleLevel = 0;
      } else {
        staffDetail?.roleAuthorityLevelResponses?.forEach((item) => {
          switch (item.featureCategory) {
            case 0:
              staffDetail.masterRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 1:
              staffDetail.customerRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 2:
              staffDetail.reservationRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 3:
              staffDetail.commonRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 4:
              staffDetail.registerRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 5:
              staffDetail.contractRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 6:
              staffDetail.revenueRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 7:
              staffDetail.inventoryRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 8:
              staffDetail.magazineRoleLevel = item?.authorityLevel ?? 0;
              break;
            case 9:
              staffDetail.approvalRoleLevel = item?.authorityLevel ?? 0;
              break;
            default:
              break;
          }
        });
      }
      dispatch(setStaffDetail({
        staffDetail,
        roles: {
          orgAccess,
          exeAccess,
          staffAccess,
          roleAccess,
          employAccess,
          customerAccess,
          customerMedicalRecord,
          customerContractHistory,
          customerAccountHistory,
          customerProductHistory,
          customerTicketHistory,
          customerReservationHistory,
          customerResponseInfo,
          customerManagerSetting,
          customerAttachedSheet,
          customerPhotoComparison,
          customerDelete,
          customerMeasuringSheet,
          customerPointManagement,
          customerCommunication,
          customerVisitInfo,
          customerSalonHistory,
          reservationManagement,
          reservationStoreSetting,
          reservationManagementSetting,
          reservationInquiry,
          reservationAreaMaster,
          reservationNewCourseMaster,
          reservationExistingCourseMaster,
          reservationAssignSetting,
          reservationMailSetting,
          reservationShiftSchedule,
          reservationShiftManagement,
          reservationAgentMaster,
          reservationCreativeMaster,
          reservationMediaPlacement,
          commonSalesMenuSection,
          commonSalesMenu,
          commonContractMenuClass,
          commonContractMenu,
          commonContractCourseClass,
          commonContractCourseCategory,
          commonContractCourse,
          commonRegisterMedium,
          commonRegisterMediumSubClass,
          commonRegisterRate,
          commonRegisterDiscount,
          commonStockPurchase,
          commonPointSetting,
          registerManagement,
          registerHeadquartersSetting,
          registerStoreSetting,
          contractStatus,
          contractCancel,
          contractCorrection,
          contractFlatRate,
          contractAutoCreateHistory,
          contractAutoTicketUseHistory,
          revenueShopBudgetSetting,
          revenueSalesDailyReport,
          revenueSalesMonthlyReport,
          revenueDailyTotal,
          revenueSalesMonthlyReportList,
          revenuePersonal,
          revenueSalesMedia,
          revenueHeadquarterBudgetSetting,
          revenueAdReport,
          revenueSalesStoreMedia,
          revenueSalesCourse,
          revenueShopItem,
          revenueExpensiveUsedCustomer,
          revenueIncentive,
          revenueContinuousRate,
          revenueReservationAdvertisingMedia,
          revenueAccountReceivable,
          revenuePointUse,
          revenuePointRemain,
          revenuePointUseRateReport,
          revenueDailyTotalList,
          revenueSimpleSalesSimulation,
          mailManagement,
          inventoryControl,
          approvalManagement,
          workflowHeadquartersSetting,
        },
      }));

      dispatch(setIsAuth(true));
      // call amount approval
      fetchSideBarCount();
      // end call approval amount
    } catch (error) {
      if (!getLocalStorage('accessRefreshToken')) {
        removeSessionStorage('companyCode');
        removeSessionStorage('companyName');
        removeSessionStorage('expirationFlag');
        removeSessionStorage('expirationDays');
        removeLocalStorage('accessToken');
        removeLocalStorage('staffId');
        removeLocalStorage('user');
        removeLocalStorage('accessRefreshToken');
        removeLocalStorage('expiredToken');
      }
    } finally {
      setIsTokenChecked(true);
    }
  };

  useEffect(() => {
    validate(accessToken);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTokenChecked]);

  return {
    isTokenChecked,
  };
};

export default useValidateToken;
